import {
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CLoadingButton,
  CDatePicker,
  CMultiSelect,
  CFormTextarea,
} from '@coreui/react-pro'
import React, { useEffect, useMemo, useState } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import AdsManagerService from 'src/service/AdsManagerService'
import ToastComponent from 'src/components/common/TaostComponent'
import PreviewImage from '../PreviewImage'

const EditForm = (props) => {
  const [sponsorDetail, setSponsorDetail] = useState({})
  const [loader, setLoader] = useState(false)
  const options = useMemo(
    () => [
      { value: 'home', text: 'Home' },
      { value: 'my_team', text: 'My Team' },
      { value: 'players', text: 'Players' },
      { value: 'rankings', text: 'Rankings' },
    ],
    [],
  )

  useEffect(() => {
    if (props.selectedId === props.sponsorId) {
      AdsManagerService.getAdsDetail(props.sponsorId).then((res) => {
        if (res.status === 200) {
          setSponsorDetail(res.data)
          const formattedPlacement = options.filter((option) =>
            res.data.placement.includes(option.value),
          )
          formik.setValues({
            title: res.data?.title || '',
            design_type: res.data?.promo_label || '',
            cta_text: res.data?.cta_text || '',
            cta_url: res.data?.cta_url || '',
            primary_color: res.data?.primary_color || '#000000',
            secondary_color: res.data?.secondary_color || '#000000',
            validity_date: res.data?.validity_date || '',
            description: res.data?.description || '',
            schedule_start_date: res.data?.schedule_start_date || '',
            schedule_end_date: res.data?.schedule_end_date || '',
            image: null,
            logo: null,
            placement: formattedPlacement,
          })
        }
      })
    }
  }, [props.selectedId, props.sponsorId])

  const SUPPORTED_FORMATS = ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']

  const validationSchema = Yup.object().shape({
    title: Yup.string()
      .required('Promo Title is required')
      .max(50, '50 Character Limit is allowed.'),

    validity_date: Yup.string()
      .required('Promo Validity date is required')
      .max(50, '50 Character Limit is allowed.'),

    placement: Yup.array()
      .min(1, 'Please select at least one promo placement')
      .required('Placement is required'),

    design_type: Yup.string()
      .required('Promo Label is required')
      .max(50, '20 Character Limit is allowed.'),

    description: Yup.string()
      .required('Promo Description is required')
      .test('word-limit', 'Maximum 50 words are allowed.', (value) => {
        if (!value) return true

        const wordCount = value
          .trim()
          .split(/\s+/)
          .filter((word) => word.length > 0).length

        return wordCount <= 50
      }),

    image: Yup.mixed()
      .nullable()
      .test('size', 'File size too large, max file size is 5 Mb', (value) => {
        if (!value) return true
        return value.size <= 1024 * 1024
      })
      .test(
        'type',
        'Invalid file format selection',
        (value) => !value || (value && SUPPORTED_FORMATS.includes(value?.type)),
      ),

    logo: Yup.mixed()
      .nullable()
      .test('size', 'File size too large, max file size is 5 Mb', (value) => {
        if (!value) return true
        return value.size <= 1024 * 1024
      })
      .test(
        'type',
        'Invalid file format selection',
        (value) => !value || (value && SUPPORTED_FORMATS.includes(value?.type)),
      ),
  })
  const formik = useFormik({
    initialValues: {
      title: '',
      design_type: '',
      cta_text: '',
      cta_url: '',
      primary_color: '#000000',
      secondary_color: '#000000',
      validity_date: '',
      description: '',
      schedule_start_date: '',
      schedule_end_date: '',
      image: null,
      logo: null,
      placement: [],
    },

    // enableReinitialize: true,
    validationSchema,

    onSubmit: (data) => {
      console.log('data.placement', data.placement)
      const formData = new FormData()

      formData.append('sponsorId', props.sponsorId)
      formData.append('title', data.title)
      formData.append('promo_label', data.design_type)
      formData.append('cta_text', data.cta_text)
      formData.append('primary_color', data.primary_color)
      formData.append('secondary_color', data.secondary_color)
      formData.append('validity_date', data.validity_date)
      formData.append('cta_url', data.cta_url)
      formData.append('schedule_start_date', data.schedule_start_date)
      formData.append('schedule_end_date', data.schedule_end_date)
      formData.append('description', data.description)
      formData.append('placement', JSON.stringify(data.placement.map((item) => item.value)))

      if (data.image) {
        formData.append('image', data.image)
      }

      if (data.logo) {
        formData.append('logo', data.logo)
      }

      setLoader(true)

      AdsManagerService.editAds(formData)
        .then((res) => {
          if (res.status === 200) {
            props.fetchUsers()
            props.toggleDetails(props.sponsorId)

            ToastComponent(res.message, 'success')
            setLoader(false)
          } else {
            setLoader(false)
            ToastComponent(res.message, 'error')
          }
        })
        .catch((e) => {
          console.log(e)
          setLoader(false)
        })
    },
  })
  console.log(options)
  console.log(formik.values.placement)
  return (
    <>
      <CForm className="row g-3" onSubmit={formik.handleSubmit}>
        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo Title *</CFormLabel>

          <CFormInput
            placeholder="Promo Title"
            className={
              'form-control' + (formik.errors.title && formik.touched.title ? ' is-invalid' : '')
            }
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            id="title"
            name="title"
          />

          {formik.errors.title && formik.touched.title && (
            <CFormFeedback invalid>{formik.errors.title}</CFormFeedback>
          )}
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo Label *</CFormLabel>

          <CFormInput
            placeholder="Promo Label"
            className={
              'form-control' +
              (formik.errors.design_type && formik.touched.design_type ? ' is-invalid' : '')
            }
            value={formik.values.design_type}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            id="design_type"
            name="design_type"
          />

          {formik.errors.design_type && formik.touched.design_type && (
            <CFormFeedback invalid>{formik.errors.design_type}</CFormFeedback>
          )}
        </CCol>

        <CCol md={12}>
          <CFormLabel className="fw-bold">Promo Details *</CFormLabel>

          <CFormTextarea
            placeholder="Promo Details"
            rows={4}
            className={
              'form-control' +
              (formik.errors.description && formik.touched.description ? ' is-invalid' : '')
            }
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            id="description"
            name="description"
          />

          {formik.errors.description && formik.touched.description && (
            <CFormFeedback invalid>{formik.errors.description}</CFormFeedback>
          )}
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo Image</CFormLabel>

          <CFormInput
            type="file"
            name="image"
            className={
              formik.touched.image
                ? formik.errors.image
                  ? 'form-control is-invalid'
                  : 'form-control is-valid'
                : 'form-control'
            }
            onChange={(event) => {
              formik.setTouched({
                ...formik.touched,
                image: true,
              })

              formik.setFieldValue('image', event.target.files[0])
            }}
          />

          {formik.touched.image && formik.errors.image ? (
            <CFormFeedback invalid>{formik.errors.image}</CFormFeedback>
          ) : null}

          <br />

          {formik.values.image ? (
            <PreviewImage width={100} height={100} file={formik.values.image} />
          ) : sponsorDetail?.image ? (
            <img
              src={`${process.env.REACT_APP_API_URL}uploads/ads/${sponsorDetail.image}`}
              alt="preview"
              width={100}
              height={100}
              style={{
                objectFit: 'cover',
                borderRadius: '8px',
              }}
            />
          ) : null}
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo Logo</CFormLabel>

          <CFormInput
            type="file"
            name="logo"
            className={
              formik.touched.logo
                ? formik.errors.logo
                  ? 'form-control is-invalid'
                  : 'form-control is-valid'
                : 'form-control'
            }
            onChange={(event) => {
              formik.setTouched({
                ...formik.touched,
                logo: true,
              })

              formik.setFieldValue('logo', event.target.files[0])
            }}
          />

          {formik.touched.logo && formik.errors.logo ? (
            <CFormFeedback invalid>{formik.errors.logo}</CFormFeedback>
          ) : null}

          <br />

          {formik.values.logo ? (
            <PreviewImage width={100} height={100} file={formik.values.logo} />
          ) : sponsorDetail?.logo ? (
            <img
              src={`${process.env.REACT_APP_API_URL}uploads/ads/${sponsorDetail.logo}`}
              alt="preview"
              width={100}
              height={100}
              style={{
                objectFit: 'cover',
                borderRadius: '8px',
              }}
            />
          ) : null}
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo CTA Text</CFormLabel>

          <CFormInput
            placeholder="Promo CTA Text"
            value={formik.values.cta_text}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            id="cta_text"
            name="cta_text"
          />
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo CTA Link</CFormLabel>

          <CFormInput
            placeholder="Promo CTA Link"
            value={formik.values.cta_url}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            id="cta_url"
            name="cta_url"
          />
        </CCol>

        <CCol md={3}>
          <CFormLabel className="fw-bold">Primary Color</CFormLabel>

          <CFormInput
            type="color"
            value={formik.values.primary_color}
            onChange={formik.handleChange}
            name="primary_color"
            id="primary_color"
          />
        </CCol>

        <CCol md={3}>
          <CFormLabel className="fw-bold">Primary Hex Code</CFormLabel>

          <CFormInput
            type="text"
            value={formik.values.primary_color}
            onChange={formik.handleChange}
            name="primary_color"
          />
        </CCol>

        <CCol md={3}>
          <CFormLabel className="fw-bold">Secondary Color</CFormLabel>

          <CFormInput
            type="color"
            value={formik.values.secondary_color}
            onChange={formik.handleChange}
            name="secondary_color"
            id="secondary_color"
          />
        </CCol>

        <CCol md={3}>
          <CFormLabel className="fw-bold">Secondary Hex Code</CFormLabel>

          <CFormInput
            type="text"
            value={formik.values.secondary_color}
            onChange={formik.handleChange}
            name="secondary_color"
          />
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo Start Date</CFormLabel>

          <CDatePicker
            locale="en-GB"
            placeholder="DD/MM/YYYY"
            inputDateFormat={(date) => {
              const day = String(date.getDate()).padStart(2, '0')
              const month = String(date.getMonth() + 1).padStart(2, '0')
              const year = date.getFullYear()

              return `${day}/${month}/${year}`
            }}
            date={
              formik.values.schedule_start_date ? new Date(formik.values.schedule_start_date) : null
            }
            onDateChange={(date) => {
              if (date) {
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')

                const formattedDate = `${year}-${month}-${day}`

                formik.setFieldValue('schedule_start_date', formattedDate)
              } else {
                formik.setFieldValue('schedule_start_date', '')
              }
            }}
          />
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo End Date</CFormLabel>

          <CDatePicker
            locale="en-GB"
            placeholder="DD/MM/YYYY"
            inputDateFormat={(date) => {
              const day = String(date.getDate()).padStart(2, '0')
              const month = String(date.getMonth() + 1).padStart(2, '0')
              const year = date.getFullYear()

              return `${day}/${month}/${year}`
            }}
            minDate={
              formik.values.schedule_start_date
                ? new Date(formik.values.schedule_start_date)
                : undefined
            }
            date={
              formik.values.schedule_end_date ? new Date(formik.values.schedule_end_date) : null
            }
            onDateChange={(date) => {
              if (date) {
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')

                const formattedDate = `${year}-${month}-${day}`

                formik.setFieldValue('schedule_end_date', formattedDate)
              } else {
                formik.setFieldValue('schedule_end_date', '')
              }
            }}
          />
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo Validity *</CFormLabel>
          <CDatePicker
            locale="en-GB"
            placeholder="DD/MM/YYYY"
            date={formik.values.validity_date ? new Date(formik.values.validity_date) : null}
            onDateChange={(date) => {
              if (date) {
                const year = date.getFullYear()
                const month = String(date.getMonth() + 1).padStart(2, '0')
                const day = String(date.getDate()).padStart(2, '0')

                const formattedDate = `${year}-${month}-${day}`

                formik.setFieldValue('validity_date', formattedDate)
              } else {
                formik.setFieldValue('validity_date', '')
              }

              formik.setFieldTouched('validity_date', true)
            }}
          />

          {formik.errors.validity_date && formik.touched.validity_date && (
            <div className="invalid-feedback d-block">{formik.errors.validity_date}</div>
          )}
        </CCol>

        <CCol md={6}>
          <CFormLabel className="fw-bold">Promo Placement *</CFormLabel>

          <CMultiSelect
            options={options}
            selectionType="tags"
            name="placement"
            value={formik.values.placement || []}
            onChange={(value) => {
              formik.setFieldValue('placement', value)
            }}
            onBlur={() => formik.setFieldTouched('placement', true)}
            className={formik.errors.placement && formik.touched.placement ? 'is-invalid' : ''}
          />

          {formik.errors.placement && formik.touched.placement && (
            <div className="invalid-feedback d-block">{formik.errors.placement}</div>
          )}
        </CCol>

        <CCol md={6}>
          <CLoadingButton type="submit" color="success" variant="outline" loading={loader}>
            Update
          </CLoadingButton>
        </CCol>
      </CForm>
    </>
  )
}

export default EditForm
